import {
  FORMAT_CCC_P,
  FORMAT_MMM_D_YYYY,
  getCurrentUnixTimestamp,
  InspectionType,
  InspectionWarrantyType,
  ReportApplianceInformationType,
  SECONDS_IN_DAY,
} from '@inspectify/core';
import { format, fromUnixTime } from "date-fns";

export type WarrantyStateType = InspectionWarrantyType & {
  maxLossDate?: string;
  inspectionWarrantyLastDayToVerifyOwnershipDate?: string;
  inspectionWarrantyExpirationDate?: string;
  inspectionWarrantyLastDayToFileClaimDate?: string;
  isInspectionWarrantyExpired: boolean;
  isInspectionWarrantyExpiringSoon: boolean;
  isInspectionWarrantyTimeToProvideProofOfOwnershipExpired: boolean;
  isInspectionWarrantyExpiredAndLastDayToClaimIsExpired: boolean;
  showClaimInspectionWarrantyBanner: boolean;
  showFileClaimBanner: boolean;
  showInspectionWarrantyExpiresSoonBanner: boolean;
  showInspectionWarrantyExpired: boolean;
  warrantyCoveredAppliances?: ReportApplianceInformationType[];
};

/**
 * Compute warranty status based on inspection reports
 * TODO: refactor gql query to a standalone query
 */
export function computeWarrantyStatus(inspectionReports: InspectionType[], orderId: string): WarrantyStateType {
  const inspectionWarranty = inspectionReports?.find(inspection => inspection?.inspectionWarranty && inspection.orderId === orderId)?.inspectionWarranty;

  if (!inspectionWarranty || orderId === '') {
    return {
      id: '', inspectionId: '', status: '',
      inspectionWarrantyLastDayToVerifyOwnershipDate: undefined,
      inspectionWarrantyExpirationDate: undefined,
      inspectionWarrantyLastDayToFileClaimDate: undefined,
      isInspectionWarrantyExpired: false,
      isInspectionWarrantyExpiringSoon: false,
      isInspectionWarrantyTimeToProvideProofOfOwnershipExpired: false,
      isInspectionWarrantyExpiredAndLastDayToClaimIsExpired: false,
      showClaimInspectionWarrantyBanner: false,
      showFileClaimBanner: false,
      showInspectionWarrantyExpiresSoonBanner: false,
      showInspectionWarrantyExpired: false,
    };
  }

  let inspectionWarrantyExpirationDate,
    inspectionWarrantyLastDayToVerifyOwnershipDate,
    inspectionWarrantyLastDayToFileClaimDate,
    isInspectionWarrantyExpired = false,
    isInspectionWarrantyExpiringSoon = false,
    isInspectionWarrantyTimeToProvideProofOfOwnershipExpired = false,
    isInspectionWarrantyExpiredAndLastDayToClaimIsExpired = false;
  const isNotClaimed = inspectionWarranty.status !== 'ownership_claimed';

  if (inspectionWarranty.startDate) {
    const startDate = new Date(inspectionWarranty.startDate).getTime() / 1000;
    const lastDayToVerifyOwnership = startDate + (SECONDS_IN_DAY * 90);
    inspectionWarrantyLastDayToVerifyOwnershipDate = format(new Date(lastDayToVerifyOwnership * 1000), FORMAT_MMM_D_YYYY);
  }

  inspectionWarrantyExpirationDate = inspectionWarranty.expirationDate;

  if (inspectionWarranty.expirationDate) {
    const expirationDate = new Date(inspectionWarranty.expirationDate).getTime() / 1000;
    const secondsIn30Days = SECONDS_IN_DAY * 30;
    const previous90Day = expirationDate - (SECONDS_IN_DAY * 90);
    const lastDateToFileClaim = expirationDate + secondsIn30Days;
    const currentUnixTimestamp = getCurrentUnixTimestamp();

    isInspectionWarrantyExpired = expirationDate < currentUnixTimestamp;
    inspectionWarrantyExpirationDate = format(expirationDate, FORMAT_CCC_P);
    inspectionWarrantyLastDayToFileClaimDate = format(lastDateToFileClaim, FORMAT_CCC_P);

    isInspectionWarrantyExpiredAndLastDayToClaimIsExpired = (
      isInspectionWarrantyExpired && lastDateToFileClaim < currentUnixTimestamp
    );

    if (!isInspectionWarrantyExpired) {
      isInspectionWarrantyExpiringSoon = previous90Day <= currentUnixTimestamp;

      if (inspectionWarranty.startDate) {
        const startDate = new Date(inspectionWarranty.startDate).getTime() / 1000;
        const next90Day = startDate + (SECONDS_IN_DAY * 90);
        isInspectionWarrantyTimeToProvideProofOfOwnershipExpired = (
          next90Day < currentUnixTimestamp && isNotClaimed
        );
      }
    }
  }
  const showClaimInspectionWarrantyBanner = !isInspectionWarrantyTimeToProvideProofOfOwnershipExpired
    && !isInspectionWarrantyExpired
    && isNotClaimed;

  const showInspectionWarrantyExpiresSoonBanner = isInspectionWarrantyExpiringSoon && !showClaimInspectionWarrantyBanner;
  const showInspectionWarrantyExpired = isInspectionWarrantyExpired && !isInspectionWarrantyExpired;
  const showFileClaimBanner = !isNotClaimed && !isInspectionWarrantyExpired && !showInspectionWarrantyExpiresSoonBanner;

  const expirationDate = new Date(inspectionWarranty?.expirationDate).getTime() / 1000;
  const today = (new Date().getTime() / 1000) + SECONDS_IN_DAY;
  const maxLossDate = format(new Date(Math.min(expirationDate, today)), FORMAT_CCC_P);

  return {
    ...inspectionWarranty,
    maxLossDate,
    inspectionWarrantyExpirationDate,
    inspectionWarrantyLastDayToVerifyOwnershipDate,
    inspectionWarrantyLastDayToFileClaimDate,
    isInspectionWarrantyExpired,
    isInspectionWarrantyExpiringSoon,
    isInspectionWarrantyTimeToProvideProofOfOwnershipExpired,
    isInspectionWarrantyExpiredAndLastDayToClaimIsExpired,
    showClaimInspectionWarrantyBanner,
    showFileClaimBanner,
    showInspectionWarrantyExpiresSoonBanner,
    showInspectionWarrantyExpired,
  };
}
